
import { Injectable, signal } from "@angular/core";
import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";
import { authConfig } from "./auth.config";

@Injectable({ providedIn: "root" })
export class AuthService {
  private readonly manager = new UserManager({
    authority: authConfig.authority,
    client_id: authConfig.client_id,
    redirect_uri: authConfig.redirect_uri,
    post_logout_redirect_uri: authConfig.post_logout_redirect_uri,
    response_type: "code",
    scope: "openid email profile josenilton-agents/jobs.analyze",
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    automaticSilentRenew: false,
  });

  readonly user = signal<User | null>(null);

  constructor() {
    this.manager.events.addUserLoaded((user) => this.user.set(user));
    this.manager.events.addUserUnloaded(() => this.user.set(null));
  }

  async currentUser(): Promise<User | null> {
    const user = await this.manager.getUser();
    const valid = user && !user.expired ? user : null;
    this.user.set(valid);
    return valid;
  }

  login(): Promise<void> {
    return this.manager.signinRedirect();
  }

  getUser(): Promise<User | null> {
    return this.manager.getUser();
  }

  async getAccessToken(): Promise<string | null> {
    const user = await this.currentUser();
    return user?.access_token ?? null;
  }

  async completeLogin(): Promise<void> {
    const user = await this.manager.signinRedirectCallback();
    this.user.set(user);
  }

  async logout(): Promise<void> {
    const cognitoDomain =
      "https://josenilton-hub.auth.us-east-1.amazoncognito.com";

    const clientId = authConfig.client_id;
    const logoutUri = `${window.location.origin}/login`;

    await this.manager.removeUser();

    const params = new URLSearchParams({
      client_id: clientId,
      logout_uri: logoutUri,
    });

    const url = `${cognitoDomain}/logout?${params.toString()}`;

    window.location.assign(url);
  }
}
